import * as React from "react";
import { GestureResponderEvent, Keyboard, StyleSheet } from "react-native";

import LBox, { LBoxProps } from "./LBox";

import LIcon from "./LIcon";
import LPressable from "./LPressable";
import { useMe } from "@hooks/trpc/useMe";
import { useLibrary } from "@hooks/trpc/useLibrary";
import { useCallback } from "react";
import { showPaywall } from "@components/Paywall";
import { modal } from "@utils/modal/useModal";
import { captureException } from "@utils/captureException";

const FREE_PLAYLIST_LIMIT = 1;

interface PremiumButtonProps {
  children: React.ReactNode;
  containerProps?: LBoxProps;
  offlineBehavior?: "disabled" | "enabled";
  override?: boolean;
  onPress?: () => void | boolean;
  premiumComponent?: React.ReactNode;
  sentryEventName: string;
  hitSlop?: number;
  /** Explicit capability gate for features whose access is not synonymous with Premium. */
  allowed?: boolean;
  onDenied?: () => void;
}

export default function PremiumButton({
  children,
  containerProps,
  override = false,
  onPress,
  premiumComponent,
  sentryEventName,
  allowed,
  onDenied,
  ...props
}: PremiumButtonProps) {
  const { isPremium } = useMe();
  const { myPlaylists } = useLibrary();

  // get the number of playlists that belongs to the user
  const playlistCount = myPlaylists.length;

  // Check if this is for playlist creation and if user still has free playlists
  const isPlaylistCreation = sentryEventName.includes("create_playlist");
  const hasFreePlaylists = playlistCount < FREE_PLAYLIST_LIMIT;
  const isAllowed =
    allowed ??
    (isPremium || override || (isPlaylistCreation && hasFreePlaylists));

  const awaitKeyboardDismiss = async () => {
    if (Keyboard.isVisible()) {
      await new Promise((resolve) => {
        const listener = Keyboard.addListener("keyboardDidHide", () => {
          listener.remove();
          resolve(true);
        });
        Keyboard.dismiss();
      });
    }
  };

  const presentSubscriptionModal = useCallback(async () => {
    await awaitKeyboardDismiss().catch((error) => {
      captureException(error, {
        channel: "breadcrumb",
        area: "ui",
        operation: "dismiss-keyboard-before-paywall",
      });
    });
    modal.closeAllModals();
    showPaywall("playlist");
  }, []);

  const handlePress = useCallback(
    (e: GestureResponderEvent) => {
      if (!isAllowed) {
        e.stopPropagation();
        e.preventDefault();
        if (onDenied) {
          onDenied();
          return;
        }
        presentSubscriptionModal().catch((error) => {
          captureException(error, {
            channel: "warning",
            area: "subscription",
            operation: "present-paywall",
          });
        });
      } else {
        onPress?.();
      }
    },
    [isAllowed, onDenied, onPress, presentSubscriptionModal],
  );

  if (isAllowed) {
    return children;
  }

  return (
    <LPressable
      {...props}
      sentryEventName={sentryEventName}
      onPress={(e) => {
        handlePress(e);
      }}
    >
      {premiumComponent ? (
        <LBox pointerEvents="none">{premiumComponent}</LBox>
      ) : (
        <>
          <LBox style={styles.crownContainer} {...containerProps}>
            <LIcon size={20} name="crown" />
          </LBox>
          <LBox pointerEvents="none">{children}</LBox>
        </>
      )}
    </LPressable>
  );
}

const styles = StyleSheet.create({
  crownContainer: {
    position: "absolute",
    top: 0,
    right: 0,
    zIndex: 10,
  },
});
